import React, { useEffect, useState } from 'react';
import { X, Save, CheckCircle2, FileText, Plus, Trash2, RefreshCw } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

// =============================================================
// CONSTANTS (mirror della landing per coerenza visiva/dati)
// =============================================================
const SECTORS = [
  ['ristorante', '🍽️ Ristorante / Pizzeria'],
  ['bar', '🍸 Bar / Cocktail Bar'],
  ['hotel', '🏨 Hotel / Resort'],
  ['gourmet', '🛒 Negozio Gourmet'],
  ['agricola', '🌾 Azienda Agricola'],
  ['distributore', '📦 Distributore'],
  ['regali', '🎁 Regali Aziendali'],
  ['ecommerce', '💻 E-commerce'],
  ['wedding', '👰 Wedding Planner'],
  ['altro', '❓ Altro'],
];

const DESTINATIONS = [
  ['rivendita', '🏪 Rivendita'],
  ['horeca', '🍸 HoReCa'],
  ['sfuso', '🏺 Sfuso / Bag-in-Box'],
  ['regali', '🎁 Regali Aziendali'],
  ['eventi', '🎊 Eventi'],
  ['altro', '❓ Altro'],
];

const CATEGORIES = [
  ['amaro', '🍂 Amaro'],
  ['limoncello', '🍋 Limoncello'],
  ['liquirizia', '🖤 Liquirizia'],
  ['finocchietto', '🌿 Finocchietto'],
  ['alloro', '🌑 Alloro/Mirto'],
  ['nocino', '🌰 Nocino'],
  ['caffe', '☕ Caffè'],
  ['bitter', '🍊 Bitter'],
  ['gin', '🌲 Gin'],
  ['grappa-barricata', '🥃 Grappa Barricata'],
  ['grappa-bianca', '🥛 Grappa Bianca'],
  ['vodka', '🔵 Vodka'],
  ['altro', '🎨 Personalizzato / Altro'],
];

const AMARO_RECIPES = [
  ['amaro-agrumato', 'Amaro Agrumato'],
  ['amaro-erbe', 'Amaro alle Erbe'],
  ['amaro-amarissimo', 'Amaro Amarissimo'],
  ['amaro-speziato', 'Amaro Speziato'],
  ['amaro-bergamotto', 'Amaro al Bergamotto DOP'],
];
const GIN_RECIPES = [
  ['gin-london-dry', 'Gin London Dry'],
  ['gin-agrumato', 'Gin Agrumato'],
];

const getRecipesFor = (catId) => {
  if (catId === 'amaro') return AMARO_RECIPES;
  if (catId === 'gin') return GIN_RECIPES;
  return [];
};

const BOTTLE_FORMATS = ['5 cl', '10 cl', '20 cl', '50 cl', '70 cl', '100 cl', '150 cl vetro', '150 cl Bag-in-Box', '300 cl Bag-in-Box'];

const ALCOHOL_RANGES = [
  ['leggero', 'Leggero (20-25%)'],
  ['medio', 'Medio (28-35%)'],
  ['forte', 'Forte (38-45%)'],
];

// =============================================================
// MAIN MODAL
// =============================================================
const AdminLeadEditModal = ({ lead, adminToken, apiUrl, onClose, onSaved, onConvertQuote }) => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);

  // Initial form state
  const [form, setForm] = useState(() => ({
    tipoCliente: lead.tipoCliente || '',
    ragioneSociale: lead.ragioneSociale || '',
    nome: lead.nome || '',
    cognome: lead.cognome || '',
    email: lead.email || '',
    telefono: lead.telefono || '',
    settori: lead.settori || [],
    settoreAltro: lead.settoreAltro || '',
    destinazione: lead.destinazione || '',
    destinazioneAltro: lead.destinazioneAltro || '',
    prodotti: (lead.prodotti || []).map((p) => ({
      categoryId: p.categoryId || 'amaro',
      customName: p.customName || '',
      recipeMode: p.recipeMode || 'standard',
      ricettaScelta: p.ricettaScelta || '',
      botaniche: p.botaniche || [],
      gradazione: p.gradazione || { range: '', precise: '' },
      noteRicetta: p.noteRicetta || '',
    })),
    formati: lead.formati || [],
    fascia: lead.fascia || '',
    quantita: lead.quantita || '',
    logoMode: lead.logoMode || '',
    logoFileName: lead.logoFileName || '',
    brandingService: lead.brandingService || false,
    noteGrafico: lead.noteGrafico || '',
    noteFinali: lead.noteFinali || '',
    admin_note: lead.admin_note || '',
  }));

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleArr = (key, val) => {
    setForm((f) => {
      const arr = f[key] || [];
      return {
        ...f,
        [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val],
      };
    });
  };

  const addProduct = () => {
    setForm((f) => ({
      ...f,
      prodotti: [...f.prodotti, { categoryId: 'amaro', customName: '', recipeMode: 'standard', ricettaScelta: '', botaniche: [], gradazione: { range: '', precise: '' }, noteRicetta: '' }],
    }));
  };

  const updateProduct = (idx, patch) => {
    setForm((f) => ({
      ...f,
      prodotti: f.prodotti.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    }));
  };

  const removeProduct = (idx) => {
    setForm((f) => ({ ...f, prodotti: f.prodotti.filter((_, i) => i !== idx) }));
  };

  const submit = async (markCompleted) => {
    if (markCompleted) setCompleting(true); else setSaving(true);
    try {
      const res = await fetch(`${apiUrl}/api/admin/landing-leads/${lead.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ ...form, mark_completed: markCompleted }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast({
          title: markCompleted ? '✅ Lead completato' : '💾 Modifiche salvate',
          description: markCompleted ? 'Il lead è ora marcato come completo e pronto per la conversione.' : null,
        });
        onSaved?.(data.lead);
      } else {
        toast({ title: 'Errore salvataggio', description: data.detail || 'Operazione fallita', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Errore', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
      setCompleting(false);
    }
  };

  const saveAndConvert = async () => {
    setCompleting(true);
    try {
      // Prima salva + marca completato
      const res = await fetch(`${apiUrl}/api/admin/landing-leads/${lead.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ ...form, mark_completed: true }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast({ title: 'Errore salvataggio', description: data.detail || '', variant: 'destructive' });
        setCompleting(false);
        return;
      }
      toast({ title: '✅ Lead completato', description: 'Ora genero il preventivo...' });
      onSaved?.(data.lead);
      // Poi triggera conversione preventivo
      await onConvertQuote?.();
    } catch (e) {
      toast({ title: 'Errore', description: e.message, variant: 'destructive' });
    } finally {
      setCompleting(false);
    }
  };

  const isBusy = saving || completing;

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-lead-edit-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-500 text-white p-5 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2">
              ✏️ Completa lead con cliente
            </h3>
            <p className="text-xs opacity-90 mt-1">
              Compila i campi in linea con il cliente al telefono. Quando hai finito, marca come completato e genera il preventivo.
            </p>
            <div className="text-xs mt-2 flex flex-wrap gap-2">
              {lead.quoteRef && (
                <span className="inline-block px-2 py-0.5 rounded-full bg-white/20">{lead.quoteRef}</span>
              )}
              {lead.last_step && (
                <span className="inline-block px-2 py-0.5 rounded-full bg-white/20">Bloccato a step {lead.last_step}/7</span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded" data-testid="admin-edit-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body scrollable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* === CONTATTO === */}
          <Section title="📞 Contatto" testid="section-contatto">
            <div className="grid grid-cols-2 gap-3">
              <FieldGroup label="Tipo cliente">
                <div className="flex gap-2">
                  <PillBtn active={form.tipoCliente === 'azienda'} onClick={() => update({ tipoCliente: 'azienda' })} testid="edit-tipo-azienda">🏢 Azienda</PillBtn>
                  <PillBtn active={form.tipoCliente === 'privato'} onClick={() => update({ tipoCliente: 'privato' })} testid="edit-tipo-privato">👤 Privato</PillBtn>
                </div>
              </FieldGroup>
              {form.tipoCliente === 'azienda' ? (
                <FieldGroup label="Ragione Sociale">
                  <input className={inputCls} value={form.ragioneSociale} onChange={(e) => update({ ragioneSociale: e.target.value })} data-testid="edit-ragione" />
                </FieldGroup>
              ) : (
                <>
                  <FieldGroup label="Nome">
                    <input className={inputCls} value={form.nome} onChange={(e) => update({ nome: e.target.value })} data-testid="edit-nome" />
                  </FieldGroup>
                  <FieldGroup label="Cognome">
                    <input className={inputCls} value={form.cognome} onChange={(e) => update({ cognome: e.target.value })} data-testid="edit-cognome" />
                  </FieldGroup>
                </>
              )}
              <FieldGroup label="Email">
                <input type="email" className={inputCls} value={form.email} onChange={(e) => update({ email: e.target.value })} data-testid="edit-email" />
              </FieldGroup>
              <FieldGroup label="Telefono">
                <input type="tel" className={inputCls} value={form.telefono} onChange={(e) => update({ telefono: e.target.value })} data-testid="edit-telefono" />
              </FieldGroup>
            </div>
          </Section>

          {/* === SETTORE === */}
          <Section title="🎯 Settore" testid="section-settore">
            <div className="flex flex-wrap gap-2">
              {SECTORS.map(([id, label]) => (
                <Chip key={id} active={form.settori.includes(id)} onClick={() => toggleArr('settori', id)} testid={`edit-sector-${id}`}>{label}</Chip>
              ))}
            </div>
            {form.settori.includes('altro') && (
              <input className={`${inputCls} mt-3`} placeholder="Specifica altro settore..." value={form.settoreAltro} onChange={(e) => update({ settoreAltro: e.target.value })} />
            )}
          </Section>

          {/* === DESTINAZIONE === */}
          <Section title="📍 Destinazione" testid="section-destinazione">
            <div className="flex flex-wrap gap-2">
              {DESTINATIONS.map(([id, label]) => (
                <Chip key={id} active={form.destinazione === id} onClick={() => update({ destinazione: id })} testid={`edit-dest-${id}`}>{label}</Chip>
              ))}
            </div>
            {form.destinazione === 'altro' && (
              <input className={`${inputCls} mt-3`} placeholder="Specifica altra destinazione..." value={form.destinazioneAltro} onChange={(e) => update({ destinazioneAltro: e.target.value })} />
            )}
          </Section>

          {/* === PRODOTTI === */}
          <Section
            title="🧪 Prodotti richiesti"
            testid="section-prodotti"
            action={
              <button onClick={addProduct} data-testid="edit-add-product" className="text-xs px-3 py-1.5 bg-purple-600 text-white rounded-md font-medium flex items-center gap-1">
                <Plus className="w-3 h-3" /> Aggiungi prodotto
              </button>
            }
          >
            {form.prodotti.length === 0 && (
              <p className="text-sm text-gray-500 italic">Nessun prodotto. Clicca &quot;Aggiungi prodotto&quot; per iniziare.</p>
            )}
            <div className="space-y-3">
              {form.prodotti.map((p, idx) => {
                const recipes = getRecipesFor(p.categoryId);
                return (
                  <div key={idx} className="rounded-lg border border-purple-200 bg-purple-50/30 p-3" data-testid={`edit-product-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-purple-700">Prodotto #{idx + 1}</span>
                      <button onClick={() => removeProduct(idx)} className="text-red-500 hover:text-red-700" data-testid={`edit-product-remove-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <FieldGroup label="Categoria">
                        <select className={inputCls} value={p.categoryId} onChange={(e) => updateProduct(idx, { categoryId: e.target.value, ricettaScelta: '' })} data-testid={`edit-product-cat-${idx}`}>
                          {CATEGORIES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                        </select>
                      </FieldGroup>
                      <FieldGroup label="Modalità">
                        <div className="flex gap-2">
                          <PillBtn active={p.recipeMode === 'standard'} onClick={() => updateProduct(idx, { recipeMode: 'standard' })} testid={`edit-product-mode-std-${idx}`}>✓ Collaudata</PillBtn>
                          <PillBtn active={p.recipeMode === 'custom'} onClick={() => updateProduct(idx, { recipeMode: 'custom' })} testid={`edit-product-mode-cust-${idx}`}>⚗️ Custom</PillBtn>
                        </div>
                      </FieldGroup>
                      {p.categoryId === 'altro' && (
                        <FieldGroup label="Nome liquore" full>
                          <input className={inputCls} value={p.customName} onChange={(e) => updateProduct(idx, { customName: e.target.value })} />
                        </FieldGroup>
                      )}
                      {p.recipeMode === 'standard' && recipes.length > 0 && (
                        <FieldGroup label="Ricetta collaudata" full>
                          <select className={inputCls} value={p.ricettaScelta} onChange={(e) => updateProduct(idx, { ricettaScelta: e.target.value })}>
                            <option value="">— scegli —</option>
                            {recipes.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                          </select>
                        </FieldGroup>
                      )}
                      {p.recipeMode === 'custom' && (
                        <>
                          <FieldGroup label="Gradazione">
                            <select className={inputCls} value={p.gradazione.range} onChange={(e) => updateProduct(idx, { gradazione: { ...p.gradazione, range: e.target.value } })}>
                              <option value="">— scegli —</option>
                              {ALCOHOL_RANGES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                            </select>
                          </FieldGroup>
                          <FieldGroup label="% precisa">
                            <input type="number" min="15" max="60" className={inputCls} value={p.gradazione.precise} onChange={(e) => updateProduct(idx, { gradazione: { ...p.gradazione, precise: e.target.value } })} />
                          </FieldGroup>
                          <FieldGroup label="Botaniche (libero)" full>
                            <input
                              className={inputCls}
                              placeholder="Es: Ginepro, Coriandolo, Bergamotto..."
                              value={Array.isArray(p.botaniche) ? p.botaniche.map((b) => b.name || b).join(', ') : ''}
                              onChange={(e) => {
                                const list = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                                updateProduct(idx, { botaniche: list.map((n) => ({ name: n, category: '', intensity: 3 })) });
                              }}
                            />
                          </FieldGroup>
                          <FieldGroup label="Note ricetta" full>
                            <textarea rows={2} className={inputCls} value={p.noteRicetta} onChange={(e) => updateProduct(idx, { noteRicetta: e.target.value })} />
                          </FieldGroup>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>

          {/* === FORMATI === */}
          <Section title="🍾 Formato & Quantità" testid="section-formato">
            <FieldGroup label="Formati (multi-select)">
              <div className="flex flex-wrap gap-2">
                {BOTTLE_FORMATS.map((f) => (
                  <Chip key={f} active={form.formati.includes(f)} onClick={() => toggleArr('formati', f)} testid={`edit-fmt-${f.replace(/\s/g, '-')}`}>{f}</Chip>
                ))}
              </div>
            </FieldGroup>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <FieldGroup label="Fascia">
                <select className={inputCls} value={form.fascia} onChange={(e) => update({ fascia: e.target.value })} data-testid="edit-fascia">
                  <option value="">— scegli —</option>
                  <option value="starter">🥉 Starter (24-120 btg)</option>
                  <option value="business">🥈 Business (120-240 btg)</option>
                  <option value="volume">🥇 Volume (240+ btg)</option>
                </select>
              </FieldGroup>
              <FieldGroup label="Quantità (n. bottiglie)">
                <input type="number" className={inputCls} value={form.quantita} onChange={(e) => update({ quantita: e.target.value })} data-testid="edit-quantita" />
              </FieldGroup>
            </div>
          </Section>

          {/* === BRANDING & NOTE === */}
          <Section title="🎨 Branding & Note" testid="section-branding">
            <FieldGroup label="Modalità logo">
              <div className="flex flex-wrap gap-2">
                <PillBtn active={form.logoMode === 'upload'} onClick={() => update({ logoMode: 'upload' })}>📁 Già fornito</PillBtn>
                <PillBtn active={form.logoMode === 'email'} onClick={() => update({ logoMode: 'email' })}>📧 Da inviare via email</PillBtn>
                <PillBtn active={form.logoMode === 'support'} onClick={() => update({ logoMode: 'support' })}>🎨 Branding da zero</PillBtn>
              </div>
            </FieldGroup>
            {form.logoMode === 'support' && (
              <label className="flex items-center gap-2 mt-2 text-sm">
                <input type="checkbox" checked={!!form.brandingService} onChange={(e) => update({ brandingService: e.target.checked })} />
                Includi servizio branding professionale nel preventivo
              </label>
            )}
            <FieldGroup label="Note grafico" full>
              <textarea rows={2} className={inputCls} value={form.noteGrafico} onChange={(e) => update({ noteGrafico: e.target.value })} />
            </FieldGroup>
            <FieldGroup label="Note finali del cliente" full>
              <textarea rows={2} className={inputCls} value={form.noteFinali} onChange={(e) => update({ noteFinali: e.target.value })} />
            </FieldGroup>
            <FieldGroup label="📝 Note admin private (interne, non visibili al cliente)" full>
              <textarea rows={2} className={inputCls} placeholder="Es: chiamata fatta il 26/06 alle 15. Cliente interessato, chiede campioni..." value={form.admin_note} onChange={(e) => update({ admin_note: e.target.value })} data-testid="edit-admin-note" />
            </FieldGroup>
          </Section>
        </div>

        {/* Footer CTA */}
        <div className="border-t bg-gray-50 p-4 flex flex-col sm:flex-row gap-2 sm:justify-between sm:items-center">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 bg-white rounded-lg text-sm font-medium hover:bg-gray-100"
            data-testid="admin-edit-cancel"
            disabled={isBusy}
          >
            Annulla
          </button>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => submit(false)}
              disabled={isBusy}
              data-testid="admin-edit-save"
              className="px-4 py-2 border border-purple-300 bg-white text-purple-700 rounded-lg text-sm font-semibold hover:bg-purple-50 flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salva modifiche
            </button>
            <button
              onClick={() => submit(true)}
              disabled={isBusy}
              data-testid="admin-edit-save-complete"
              className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 flex items-center gap-2 disabled:opacity-50"
            >
              {completing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Salva e marca Completato
            </button>
            {onConvertQuote && !lead.quote_id && (
              <button
                onClick={saveAndConvert}
                disabled={isBusy}
                data-testid="admin-edit-save-and-quote"
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50"
              >
                {completing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                Salva + Genera Preventivo
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================
// SUB
// =============================================================
const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500';

function Section({ title, children, testid, action }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-gray-200 p-4 bg-white">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-bold text-sm text-purple-700 uppercase tracking-wide">{title}</h4>
        {action}
      </div>
      {children}
    </div>
  );
}

function FieldGroup({ label, children, full }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function PillBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${active ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'}`}
    >
      {children}
    </button>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${active ? 'bg-purple-100 text-purple-800 border-purple-400 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
    >
      {children}
    </button>
  );
}

export default AdminLeadEditModal;
